"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";
import { useRef, useEffect, useState, type FormEvent } from "react";
import { useCart } from "~/app/_components/CartContext";
import { DeploymentAssetImage } from "~/app/_components/DeploymentAssetImage";
import { useDeploymentConfig } from "~/app/_components/ConfigProvider";
import { useAppRouter } from "~/app/_components/useAppRouter";
import {
  DEPLOYMENT_ASSET_FILES,
  remoteDeploymentAssetUrl,
} from "~/lib/deployment-assets";
import { searchPageHref } from "~/lib/catalog-query";
import { useMobileNavOptional } from "~/app/_components/MobileNavContext";
import type { Product } from "~/data/products";

const SEARCH_DEBOUNCE_MS = 350;

export function Header() {
  const router = useAppRouter();
  const { data: session, status } = useSession();
  const { openCart, totalCount } = useCart();
  const { assetsBaseUrl } = useDeploymentConfig();
  const mobileNav = useMobileNavOptional();
  const categoriesOpen = mobileNav?.isOpen ?? false;
  const logoRemoteUrl = remoteDeploymentAssetUrl(
    assetsBaseUrl,
    DEPLOYMENT_ASSET_FILES.logo,
  );
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [userMenuOpen]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    const ac = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `/api/customer-products?${new URLSearchParams({
            search: q,
            limit: "8",
            offset: "0",
          })}`,
          { signal: ac.signal },
        );
        if (!res.ok) throw new Error("Search failed");
        const json = (await res.json()) as { data?: Product[] };
        setSearchResults(Array.isArray(json.data) ? json.data : []);
      } catch (e) {
        if ((e as Error).name === "AbortError") return;
        setSearchResults([]);
      } finally {
        if (!ac.signal.aborted) setSearchLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [searchQuery]);

  useEffect(() => {
    function handlePointerDown(e: MouseEvent) {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(e.target as Node)
      ) {
        setSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  function goToSearchPage(query: string) {
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    router.push(searchPageHref({ search: trimmed }));
  }

  function handleSearchSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    goToSearchPage(searchQuery);
  }

  return (
    <header className="sticky top-0 z-50 border-b border-stone-200 bg-white/95 backdrop-blur">
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] grid-rows-[auto_auto] items-center gap-x-2 gap-y-2 px-3 py-3 sm:gap-x-3 sm:px-4 sm:py-3.5 lg:grid-rows-1 lg:gap-x-4 lg:gap-y-0 lg:px-6 lg:py-4">
        {mobileNav ? (
          <button
            type="button"
            onClick={mobileNav.toggle}
            className="col-start-1 row-start-1 -ml-1 rounded-lg p-2 text-stone-700 hover:bg-stone-100 lg:hidden"
            aria-expanded={categoriesOpen}
            aria-controls="mobile-categories-panel"
            aria-label={
              categoriesOpen ? "Close categories menu" : "Open categories menu"
            }
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        ) : null}

        <Link
          href="/"
          className={`flex min-w-0 items-center gap-2 font-semibold text-stone-900 ${
            mobileNav
              ? "col-start-2 row-start-1 lg:col-start-1"
              : "col-start-1 row-start-1"
          }`}
        >
          <div className="relative h-10 w-10 shrink-0 sm:h-11 sm:w-11">
            <DeploymentAssetImage
              filename={DEPLOYMENT_ASSET_FILES.logo}
              remoteUrl={logoRemoteUrl}
              alt=""
              fill
              className="object-contain"
              sizes="(min-width: 640px) 44px, 40px"
            />
          </div>
          <span className="truncate text-lg sm:text-xl">Diaspora One</span>
        </Link>

        <nav className="col-start-3 row-start-1 flex shrink-0 items-center justify-end gap-2 sm:gap-4">
          <Link
            href="/"
            className="hidden text-sm font-medium text-stone-600 hover:text-stone-900 sm:inline"
          >
            Home
          </Link>
          <Link
            href="/about"
            className="hidden text-sm font-medium text-stone-600 hover:text-stone-900 sm:inline"
          >
            About
          </Link>
          <button
            type="button"
            onClick={openCart}
            className="relative inline-flex cursor-pointer text-sm font-medium text-stone-600 hover:text-stone-900"
            aria-label={`Cart, ${totalCount} items`}
          >
            Cart
            {totalCount > 0 && (
              <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-red px-1 text-xs font-medium text-white">
                {totalCount > 99 ? "99+" : totalCount}
              </span>
            )}
          </button>
          {status === "loading" ? (
            <span className="text-sm text-stone-400">...</span>
          ) : session?.user ? (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setUserMenuOpen((open) => !open)}
                className="flex items-center gap-1 text-sm font-medium text-stone-600 hover:text-stone-900"
                aria-expanded={userMenuOpen}
                aria-haspopup="true"
                aria-label="User menu"
              >
                User
                <svg
                  className={`h-4 w-4 transition ${userMenuOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {userMenuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 min-w-[140px] rounded-lg border border-stone-200 bg-white py-1 shadow-lg"
                  role="menu"
                >
                  <Link
                    href="/"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  >
                    Home
                  </Link>
                  <Link
                    href="/orders"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  >
                    Orders
                  </Link>
                  <Link
                    href="/profile"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  >
                    Profile
                  </Link>
                  <Link
                    href="/signout"
                    role="menuitem"
                    onClick={() => setUserMenuOpen(false)}
                    className="btn-primary block px-4 py-2 text-center text-sm"
                  >
                    Logout
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link
                href="/login"
                className="text-sm font-medium text-stone-600 hover:text-stone-900"
              >
                Log in
              </Link>
              <Link
                href="/signup"
                className="hidden text-sm font-medium text-stone-600 hover:text-stone-900 sm:inline"
              >
                Sign up
              </Link>
            </>
          )}
        </nav>

        <div
          ref={searchWrapRef}
          className="relative col-span-3 row-start-2 w-full lg:col-span-1 lg:col-start-2 lg:row-start-1 lg:max-w-xl lg:px-3 lg:justify-self-center"
        >
          <form onSubmit={handleSearchSubmit}>
            <label className="sr-only" htmlFor="search">
              Search
            </label>
            <input
              id="search"
              type="search"
              autoComplete="off"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              className="w-full rounded-full border border-amber-500 bg-white px-5 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 focus:border-amber-600 focus:outline-none focus:ring-1 focus:ring-amber-500"
              aria-expanded={searchOpen}
              aria-controls="search-results"
              aria-autocomplete="list"
            />
          </form>
          {searchOpen && searchQuery.trim() !== "" && (
            <div
              id="search-results"
              role="listbox"
              aria-label="Search results"
              className="absolute left-0 right-0 top-full z-50 mt-1 overflow-hidden rounded-xl border border-stone-200 bg-white py-1 shadow-lg lg:left-1/2 lg:w-full lg:max-w-md lg:-translate-x-1/2"
            >
              {searchLoading ? (
                <p className="px-4 py-3 text-sm text-stone-500">Searching…</p>
              ) : searchResults.length > 0 ? (
                <ul className="max-h-72 overflow-y-auto">
                  {searchResults.map((p) => (
                    <li key={p.id} role="option">
                      <Link
                        href={`/product/${p.id}`}
                        prefetch={false}
                        onClick={() => {
                          setSearchOpen(false);
                          setSearchQuery("");
                          setSearchResults([]);
                        }}
                        className="flex flex-col gap-0.5 px-4 py-2.5 text-left hover:bg-stone-50"
                      >
                        <span className="text-sm font-medium text-stone-900">{p.name}</span>
                        {p.price ? (
                          <span className="text-xs text-stone-500">{p.price}</span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <button
                      type="button"
                      onClick={() => goToSearchPage(searchQuery)}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-amber-700 hover:bg-stone-50"
                    >
                      View all results
                    </button>
                  </li>
                </ul>
              ) : (
                <button
                  type="button"
                  onClick={() => goToSearchPage(searchQuery)}
                  className="w-full px-4 py-3 text-left text-sm font-medium text-amber-700 hover:bg-stone-50"
                >
                  Search for “{searchQuery.trim()}”
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
